/**
 * 匿名玩家 session（C02 §3、C03 §2）。
 *
 * ★ 儲存規則（C03 §5，不可違反）：
 *   token 存 **記憶體 + sessionStorage**，**絕不放 localStorage**。
 *   理由：localStorage 是跨分頁、跨開關瀏覽器持久的；一旦有 XSS，被偷走的 token
 *   可以被長期利用。sessionStorage 只活在這個分頁，關掉就沒了，攻擊窗口小得多。
 *   guest token 本身壽命 24h（C03 §2），我們額外在到期前 60s 就視為失效並重新申請。
 */

import type { GuestTokenResponse } from '../api/types';

export interface GuestSession {
  token: string;
  subjectId: string;
  displayName: string;
  expiresAt: string;
}

const EXPIRY_SKEW_MS = 60_000;

export class GuestSessionStore {
  private readonly key: string;
  private readonly storage: Storage | null;
  /** 記憶體是第一手來源；sessionStorage 只是為了 F5 之後還在。 */
  private memory: GuestSession | null = null;
  private listeners = new Set<(s: GuestSession | null) => void>();

  constructor(storagePrefix: string, storage: Storage | null = safeSessionStorage()) {
    this.key = `${storagePrefix}guest`;
    this.storage = storage;
    this.memory = this.readFromStorage();
  }

  private readFromStorage(): GuestSession | null {
    if (!this.storage) return null;
    try {
      const raw = this.storage.getItem(this.key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<GuestSession>;
      if (!parsed.token || !parsed.subjectId || !parsed.expiresAt) return null;
      const s: GuestSession = {
        token: parsed.token,
        subjectId: parsed.subjectId,
        displayName: parsed.displayName ?? '',
        expiresAt: parsed.expiresAt,
      };
      return isUsable(s) ? s : null;
    } catch {
      return null;
    }
  }

  get(): GuestSession | null {
    if (this.memory && !isUsable(this.memory)) this.clear();
    return this.memory;
  }

  getToken(): string | null {
    return this.get()?.token ?? null;
  }

  set(resp: GuestTokenResponse): GuestSession {
    const session: GuestSession = {
      token: resp.guest_token,
      subjectId: resp.subject_id,
      displayName: resp.display_name,
      expiresAt: resp.expires_at,
    };
    this.memory = session;
    try {
      this.storage?.setItem(this.key, JSON.stringify(session));
    } catch {
      /* storage 滿了或被停用：記憶體那份還在，功能不受影響 */
    }
    this.emit();
    return session;
  }

  clear(): void {
    this.memory = null;
    try {
      this.storage?.removeItem(this.key);
    } catch {
      /* ignore */
    }
    this.emit();
  }

  subscribe(fn: (s: GuestSession | null) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this.memory);
  }
}

export function isUsable(s: GuestSession): boolean {
  const exp = Date.parse(s.expiresAt);
  if (Number.isNaN(exp)) return false;
  return exp - EXPIRY_SKEW_MS > Date.now();
}

function safeSessionStorage(): Storage | null {
  try {
    if (typeof globalThis.sessionStorage === 'undefined') return null;
    // Safari 無痕模式會讓 setItem 直接丟例外，先探一次。
    const probe = '__elabgaming_probe__';
    globalThis.sessionStorage.setItem(probe, '1');
    globalThis.sessionStorage.removeItem(probe);
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}
